/**
 * TrackingLine Component
 * Renders the red vertical tracking line with date label with responsive support
 */

import React from 'react';
import { Box } from '@mui/material';
import type { MousePosition } from '../types';
import { formatTooltipDate } from '../utils';
import {
  TRACKING_LINE_WIDTH,
  TRACKING_LINE_CLICKABLE_WIDTH,
} from '../constants';

interface TrackingLineProps {
  mousePosition: MousePosition;
  isCtrlPressed: boolean;
  chartHeight: number;
  onRedLineClick: (e: React.MouseEvent) => void;
  padding?: { top: number; right: number; bottom: number; left: number };
}

export const TrackingLine: React.FC<TrackingLineProps> = ({
  mousePosition,
  isCtrlPressed,
  chartHeight,
  onRedLineClick,
  padding = { top: 20, right: 20, bottom: 70, left: 180 },
}) => {
  if (!mousePosition.date) return null;

  return (
    <g>
      {/* Invisible wider clickable area for the red line */}
      <line
        x1={padding.left + mousePosition.x}
        y1={padding.top}
        x2={padding.left + mousePosition.x}
        y2={chartHeight - padding.bottom}
        stroke="transparent"
        strokeWidth={TRACKING_LINE_CLICKABLE_WIDTH}
        onClick={onRedLineClick}
        style={{ cursor: isCtrlPressed ? 'zoom-out' : 'zoom-in' }}
      />
      {/* Visible red line */}
      <line
        x1={padding.left + mousePosition.x}
        y1={padding.top}
        x2={padding.left + mousePosition.x}
        y2={chartHeight - padding.bottom}
        stroke="#ef4444"
        strokeWidth={TRACKING_LINE_WIDTH}
        opacity={0.8}
        pointerEvents="none"
      />
      {/* Date label at bottom using foreignObject for auto-width */}
      <foreignObject
        x={padding.left + mousePosition.x - 100}
        y={chartHeight - padding.bottom + 30}
        width={200}
        height={24}
        style={{ overflow: 'visible', pointerEvents: 'none' }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#ef4444',
            color: '#ffffff',
            padding: '4px 12px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 600,
            fontFamily: 'system-ui, -apple-system, sans-serif',
            opacity: 0.9,
            whiteSpace: 'nowrap',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
            margin: '0 auto',
            width: 'fit-content',
          }}
        >
          {formatTooltipDate(mousePosition.date)}
        </Box>
      </foreignObject>
    </g>
  );
};
